import { getRuntimeConfigValue } from './runtimeConfig';
import { createSessionAbortError, isAbortError } from './sessionLifecycle';

const DEFAULT_API_BASE = '/api';

type CreateApiFetchOptions = {
  getAuthToken?: () => Promise<string | null>;
  /**
   * Returns the AbortSignal of the session that is active when a request starts.
   * The request is cancelled when that session ends, and is never sent if the
   * session changed while the bearer token was being resolved, so a request is
   * always owned by the identity that initiated it.
   */
  getSessionSignal?: () => AbortSignal | undefined;
  defaultHeaders?: Record<string, string>;
};

/**
 * How a successful (2xx) response body is read:
 * - `json` (default): the endpoint must answer with a JSON document. An empty or
 *   malformed body is a protocol error, never a silent success.
 * - `optional-json`: a 204/205 status or a zero-length body resolves to
 *   `undefined`; anything else must be valid JSON.
 * - `empty`: the endpoint has no useful body; it is discarded and `undefined`
 *   is returned, whatever the 2xx response carries.
 * Blob, text and streaming downloads are intentionally not served by this
 * client: they keep their dedicated adapters (see `downloadPortalMeetingIcs`).
 */
export type ApiResponseMode = 'json' | 'optional-json' | 'empty';

/**
 * Request contract of the shared client. The body is a pre-serialised string
 * (the client defaults to a JSON Content-Type); FormData, Blob and stream
 * bodies are not supported here and are rejected before anything is sent.
 */
export type ApiRequestInit = Omit<RequestInit, 'body'> & {
  body?: string | null;
  response?: ApiResponseMode;
};

export type ApiFetch = <T>(
  path: string,
  request?: ApiRequestInit,
  auth?: boolean
) => Promise<T>;

/** Machine codes the client itself assigns (the server never sends them). */
export const API_ERROR_CODES = {
  /** A 2xx response whose body does not honour the declared response mode. */
  invalidResponse: 'INVALID_RESPONSE',
  /** An error status whose body is not the JSON error envelope (HTML, proxy page, empty). */
  nonJsonError: 'NON_JSON_ERROR',
  /** A request body or response mode the shared client does not support. */
  unsupportedRequest: 'UNSUPPORTED_REQUEST',
} as const;

export type ApiFieldErrors = Readonly<Record<string, readonly string[]>>;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    /** Per-field validation messages, only when the server sent the supported `details.fieldErrors` shape. */
    public readonly fieldErrors?: ApiFieldErrors,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * True when the server answered with a success status but the body could not
 * be honoured. The write may have been committed: it must be re-read or
 * confirmed by the user, never replayed automatically.
 */
export function isApiProtocolError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === API_ERROR_CODES.invalidResponse;
}

export function getApiBaseUrl(): string {
  return getRuntimeConfigValue('API_BASE_URL', import.meta.env.VITE_API_BASE_URL || DEFAULT_API_BASE);
}

function throwIfSessionEnded(signal: AbortSignal | undefined) {
  if (signal?.aborted) throw createSessionAbortError();
}

function mergeSignals(
  sessionSignal: AbortSignal | undefined,
  requestSignal: AbortSignal | null | undefined,
): AbortSignal | undefined {
  if (!sessionSignal) return requestSignal ?? undefined;
  if (!requestSignal) return sessionSignal;
  // Manual merge instead of AbortSignal.any: that API is missing from Safari < 17.4.
  const merged = new AbortController();
  const forward = (source: AbortSignal) => () => merged.abort(source.reason);
  if (requestSignal.aborted) merged.abort(requestSignal.reason);
  else requestSignal.addEventListener('abort', forward(requestSignal), { once: true });
  sessionSignal.addEventListener('abort', forward(sessionSignal), { once: true });
  return merged.signal;
}

const RESPONSE_MODES: readonly ApiResponseMode[] = ['json', 'optional-json', 'empty'];
const MAX_FIELD_ERRORS = 50;
const MAX_FIELD_MESSAGES = 10;
const MAX_FIELD_MESSAGE_LENGTH = 300;

type HeaderEntries = Map<string, [name: string, value: string]>;

function putHeader(entries: HeaderEntries, name: string, value: string) {
  entries.set(name.toLowerCase(), [name, value]);
}

/**
 * Merges a HeadersInit (plain record, native Headers or tuple array) into the
 * case-insensitive map. Later inputs override earlier ones by header name,
 * whatever casing either side used; a plain record keeps the casing it used.
 */
function mergeHeaders(entries: HeaderEntries, init: HeadersInit | undefined) {
  if (!init) return;
  if (Array.isArray(init) || init instanceof Headers) {
    // The native class validates names/values and combines repeated tuple names.
    new Headers(init).forEach((value, name) => putHeader(entries, name, value));
    return;
  }
  Object.entries(init).forEach(([name, value]) => {
    if (value != null) putHeader(entries, name, String(value));
  });
}

function unsupported(message: string): ApiError {
  return new ApiError(message, 0, API_ERROR_CODES.unsupportedRequest);
}

function readFieldErrors(payload: unknown): ApiFieldErrors | undefined {
  const details = (payload as { details?: unknown } | null)?.details;
  const raw = (details as { fieldErrors?: unknown } | null | undefined)?.fieldErrors;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const result: Record<string, readonly string[]> = {};
  for (const [field, messages] of Object.entries(raw).slice(0, MAX_FIELD_ERRORS)) {
    if (!Array.isArray(messages)) continue;
    const safe = messages
      .filter((message): message is string => typeof message === 'string')
      .slice(0, MAX_FIELD_MESSAGES)
      .map((message) => message.slice(0, MAX_FIELD_MESSAGE_LENGTH));
    if (safe.length) result[field] = safe;
  }
  return Object.keys(result).length ? result : undefined;
}

async function toApiError(response: Response, signal: AbortSignal | undefined): Promise<ApiError> {
  let payload: unknown;
  let parsed = true;
  try {
    payload = await response.json();
  } catch (error) {
    // Cancelled while the error body streamed: surface the cancellation, not a server failure.
    if (signal?.aborted || isAbortError(error)) throw error;
    parsed = false;
  }
  if (!parsed) {
    // HTML, proxy pages or empty bodies: never echo the body, which may be markup or backend detail.
    return new ApiError(
      `Request failed (HTTP ${response.status}): the server returned an unexpected non-JSON response`,
      response.status,
      API_ERROR_CODES.nonJsonError,
    );
  }
  const error = payload as { error?: unknown; code?: unknown } | null;
  return new ApiError(
    typeof error?.error === 'string' && error.error ? error.error : 'Request failed',
    response.status,
    typeof error?.code === 'string' ? error.code : undefined,
    readFieldErrors(payload),
  );
}

function protocolError(response: Response, reason: string): ApiError {
  return new ApiError(
    `The server answered HTTP ${response.status} but ${reason}`,
    response.status,
    API_ERROR_CODES.invalidResponse,
  );
}

function isNoContent(response: Response): boolean {
  return response.status === 204 || response.status === 205;
}

async function readSuccess(
  response: Response,
  mode: ApiResponseMode,
  signal: AbortSignal | undefined,
): Promise<unknown> {
  if (mode === 'empty') {
    // Release the connection without parsing: the body is not part of the contract.
    await response.body?.cancel().catch(() => undefined);
    return undefined;
  }
  try {
    if (mode === 'json') {
      if (isNoContent(response)) throw protocolError(response, 'sent no content where a JSON body was required');
      return await response.json();
    }
    if (isNoContent(response)) return undefined;
    const text = await response.text();
    return text.trim() ? JSON.parse(text) : undefined;
  } catch (error) {
    // A cancellation while the body streams is the caller's/session's decision, not a server fault.
    if (signal?.aborted || isAbortError(error)) throw error;
    if (error instanceof ApiError) throw error;
    throw protocolError(response, 'the body is not valid JSON');
  }
}

export function createApiFetch(options: CreateApiFetchOptions = {}) {
  return async function apiFetch<T>(
    path: string,
    request: ApiRequestInit = {},
    auth: boolean = Boolean(options.getAuthToken)
  ): Promise<T> {
    const { response: responseMode = 'json', ...init } = request;
    if (!RESPONSE_MODES.includes(responseMode)) {
      throw unsupported(`Unsupported API response mode: ${String(responseMode)}`);
    }
    if (init.body != null && typeof init.body !== 'string') {
      throw unsupported('Unsupported API request body: the shared client only sends pre-serialised string bodies');
    }
    const base = getApiBaseUrl();
    const sessionSignal = options.getSessionSignal?.();
    throwIfSessionEnded(sessionSignal);
    // Precedence, lowest to highest: JSON default, client default headers,
    // the session bearer token, then the caller's own headers.
    const headers: HeaderEntries = new Map();
    putHeader(headers, 'Content-Type', 'application/json');
    mergeHeaders(headers, options.defaultHeaders);

    if (auth && options.getAuthToken) {
      const token = await options.getAuthToken();
      throwIfSessionEnded(sessionSignal);
      if (token) {
        putHeader(headers, 'Authorization', `Bearer ${token}`);
      }
    }
    mergeHeaders(headers, init.headers);

    const signal = mergeSignals(sessionSignal, init.signal);
    const response = await fetch(`${base}${path}`, {
      ...init,
      ...(signal ? { signal } : {}),
      headers: Object.fromEntries(headers.values()),
    });

    if (!response.ok) {
      throw await toApiError(response, signal);
    }

    return (await readSuccess(response, responseMode, signal)) as T;
  };
}
